'use client';
import { useLang } from '@/i18n';
import { subscribeCookie } from '@/utils/cookies/subscribeCookie';
import gtm from '@/utils/gtm';
import { useScheduledContent } from '@/utils/hooks/useScheduledContent';
import EcoImg from 'eco-design/base/eco-img';
import EditorLink from 'lowcode/components/EditorLink';
import { fetchPost } from 'lowcode/utils/service/FetchClient';
import { baseUrl } from 'lowcode/utils/service/baseUrl';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import styles from './index.module.scss';

const MoreIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
        <path d="M6.83337 4.47156L10.8334 8.47156L6.83337 12.4716" stroke="#3F68E0" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export default function TheComp() {
    const { t } = useLang('rapidseries.compC');
    const { locale = 'us' } = useRouter();
    const tags = [t('tags.charging'), t('tags.power'), t('tags.personality')];
    const [input, setInput] = useState<string>('');
    const [tip, setTip] = useState<string>('');
    const [searchParams, setSearchParams] = useState('');
    const [isAgreed, setIsAgreed] = useState(['us', 'ca'].includes(locale));

    const buy = useScheduledContent({
        '2025/06/24 15:00:00 GMT+8': t('btn3'),
        '2025/07/15 15:00:00 GMT+8': t('btn2'),
    });

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setSearchParams(window.location.search);
        }
    }, []);

    const handleLearnMore = () => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'select_product',
            list_name: t('title'),
            product_name: 'EcoFlow RAPID Series',
            button_name: t('learn_more'),
        });
    };
    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value);
    };

    const handleFocus = () => {
        if (tip) setTip('');
    };

    const handleBlur = () => {
        const email = input.trim();
        if (!email) {
            setTip(t('subscribe_empty'));
            return;
        }
        const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (!regex.test(email)) {
            setTip(t('subscribe_error'));
            return;
        }
        if (!isAgreed) {
            setTip(t('subscribe_agreement'));
            return;
        }
        return true;
    };
    const handleSearch = async () => {
        if (!handleBlur()) return;

        const email = input.trim();
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;

        const configs = {
            us: {
                contactListId: ['986635575'],
                fields: [
                    { id: '4423', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: false },
                    { id: '11150', type: 'multiple', value: ['15'], overwrite: false },
                    { id: '12637', type: 'text', value: dateString, overwrite: false },
                ],
            },
            ca: {
                contactListId: ['2097466008'],
                fields: [
                    { id: '4424', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: false },
                    { id: '11150', type: 'multiple', value: ['15'], overwrite: false },
                    { id: '12637', type: 'text', value: dateString, overwrite: false },
                ],
            },
            jp: {
                contactListId: ['503090051'],
                fields: [
                    { id: '4431', type: 'single', value: 1, overwrite: true },
                    { id: '31', type: 'single', value: 1, overwrite: false },
                    { type: 'multiple', id: '12713', value: ['2'], overwrite: false },
                    { type: 'text', id: '12712', value: dateString, overwrite: false },
                ],
            },
        };

        const { contactListId, fields } = configs[locale as keyof typeof configs] || configs['jp'];

        const res = await fetchPost(
            `${baseUrl(locale)}/subscribe/new`,
            locale,
            {
                contactListId: contactListId[0],
                email,
                fields,
                source: 'WEB_NEW_PRODUCT_SUB',
                otherContactListIds: contactListId.slice(1),
                eventTracking: subscribeCookie({
                    sector_name: t('title'),
                    entry_position: 3,
                }),
            },
            {
                headers: {
                    apiKey: '527760f6df93c0d6e98609fa8e8a1f4a',
                },
            },
        );

        if (res?.code === '0') {
            setTip(t('subscribe_success'));
            setInput('');
            setTimeout(() => {
                setTip('');
            }, 3000);
        } else {
            setTip(res?.message);
            console.error(res?.message);
        }

        gtm.push({
            event: 'uaEvent',
            event_name: 'product_submit',
            button_name: t('submit_button'),
            product_name: t('title'),
            status: res?.code === '0' ? 'success' : 'fail',
            tag: 'Agree',
        });
    };

    return (
        <div id="Screen2" className={`${styles.TheComp} temp-Screen2`}>
            <div className="banner">
                <div className="content">
                    <h2 className="subtitle">{t('subtitle')}</h2>
                    <h1 className="title">{t('title')}</h1>
                    <div className="tags">
                        {tags.map((tag, index) => (
                            <span key={tag}>
                                {tag}
                                {index < tags.length - 1 && ' | '}
                            </span>
                        ))}
                    </div>
                    {!['de', 'fr', 'it', 'es', 'uk', 'eu'].includes(locale) && (
                        <>
                            <div className="more-link">
                                <EditorLink href={`/${locale}/rapid-power-bank${searchParams}`} className="learnMore" target="_blank" onClick={handleLearnMore}>
                                    {t('learn_more')}
                                </EditorLink>
                                <MoreIcon />
                            </div>

                            {
                                <div className="searchContainer">
                                    <input
                                        type="text"
                                        placeholder={t('search_placeholder')}
                                        className="searchInput"
                                        value={input}
                                        onChange={handleInput}
                                        onFocus={handleFocus}
                                        onBlur={handleBlur}
                                    />
                                    <button className="searchButton" onClick={handleSearch}>
                                        {t('submit_button')}
                                    </button>
                                </div>
                            }
                            {['jp'].includes(locale) && (
                                <div className="agreement-box">
                                    <input type="checkbox" id="agreement" checked={isAgreed} onChange={(e) => setIsAgreed(e.target.checked)} />
                                    <label className="agreement">
                                        {t.rich('agreement', {
                                            link1: (tx: string) => (
                                                <EditorLink
                                                    href="https://account.ecoflow.com/agreement/ja-jp/PrivacyPolicy.html"
                                                    className="agreement-link"
                                                    target="_blank"
                                                >
                                                    {tx}
                                                </EditorLink>
                                            ),
                                            link2: (tx: string) => (
                                                <EditorLink
                                                    href="https://account.ecoflow.com/agreement/ja-jp/TermsOfUse.html"
                                                    className="agreement-link"
                                                    target="_blank"
                                                >
                                                    {tx}
                                                </EditorLink>
                                            ),
                                        })}
                                    </label>
                                </div>
                            )}
                            {tip && <p className={`tip${t('subscribe_success') === tip ? ' success' : ''}`}>{tip}</p>}
                        </>
                    )}
                    {['eu', 'uk', 'de', 'fr', 'it', 'es'].includes(locale) && (
                        <>
                            <div className="buttonContainer">
                                <EditorLink
                                    href={`/${locale}/rapid-power-bank${searchParams}`}
                                    className="button learnMoreBtn"
                                    target="_blank"
                                    dataLayer={{
                                        event: 'uaEvent',
                                        event_name: 'select_product',
                                        list_name: t('title'),
                                        product_name: 'EcoFlow RAPID Series',
                                        button_name: t('btn1'),
                                    }}
                                >
                                    {t('btn1')}
                                </EditorLink>
                                <EditorLink
                                    href={`https://${locale}.ecoflow.com/collections/power-bank?sort_by=best-selling&filter.p.m.custom.filter_series=RAPID`}
                                    className="button buyNowBtn"
                                    target="_blank"
                                    dataLayer={{
                                        event: 'uaEvent',
                                        event_name: 'buy',
                                        product_name: 'EcoFlow RAPID Series',
                                        position: t('title'),
                                    }}
                                >
                                    {buy}
                                </EditorLink>
                            </div>
                        </>
                    )}
                </div>
                <div className="imageContainer">
                    <EcoImg
                        pc="https://websiteoss.ecoflow.com/upload/image/1749200253515/pc_kv2.png"
                        h5="https://websiteoss.ecoflow.com/upload/image/1749199062073/m_kv2.png"
                    />
                </div>
            </div>
        </div>
    );
}
